import { MEMBERSHIP_ENTITLEMENTS, type MembershipTier } from "./entitlements.js";

export type StoreProvider = "APPLE_APP_STORE" | "GOOGLE_PLAY";
export type StoreLifecycleStatus =
  | "ACTIVE"
  | "GRACE"
  | "CANCELS_AT_PERIOD_END"
  | "DOWNGRADE_SCHEDULED"
  | "REFUNDED"
  | "REVERSED"
  | "CHARGEBACK"
  | "LIFETIME";

export interface StoreSubscriptionState {
  readonly tier: MembershipTier;
  readonly status: StoreLifecycleStatus;
  readonly provider: StoreProvider | "OWNER_DIRECT" | null;
  readonly paidThrough: string | null;
  readonly billingAnchor: string | null;
  readonly graceEndsAt: string | null;
  readonly scheduledTier: MembershipTier | null;
  readonly eventIds: readonly string[];
}

export type StoreLifecycleEvent =
  | { readonly type: "PURCHASE" | "RENEWAL" | "RESTORE"; readonly id: string; readonly provider: StoreProvider; readonly at: string; readonly tier: "INSIDER" | "CREATOR"; readonly paidThrough: string; readonly amountMinor: 2500 | 5000 }
  | { readonly type: "UPGRADE"; readonly id: string; readonly provider: StoreProvider; readonly at: string; readonly tier: "INSIDER" | "CREATOR"; readonly paidThrough: string; readonly amountMinor: 2500 | 5000; readonly prorationCreditMinor: 0 }
  | { readonly type: "DOWNGRADE_SCHEDULED"; readonly id: string; readonly provider: StoreProvider; readonly at: string; readonly tier: "FREE" | "INSIDER"; readonly effectiveAt: string }
  | { readonly type: "CANCEL"; readonly id: string; readonly provider: StoreProvider; readonly at: string }
  | { readonly type: "PERIOD_ENDED"; readonly id: string; readonly provider: StoreProvider; readonly at: string }
  | { readonly type: "PAYMENT_FAILED"; readonly id: string; readonly provider: StoreProvider; readonly at: string }
  | { readonly type: "GRACE_EXPIRED" | "REFUND" | "REVERSAL" | "CHARGEBACK"; readonly id: string; readonly provider: StoreProvider; readonly at: string }
  | { readonly type: "LIFETIME_OWNER_GRANT"; readonly id: string; readonly at: string; readonly ownerDirect: true; readonly referralCommissionMinor: 0 };

export interface SandboxStoreFixture {
  readonly fixtureId: string;
  readonly sandboxOnly: true;
  readonly source: "apple_sandbox" | "google_play_sandbox";
  readonly productionUse: "FORBIDDEN";
  readonly events: readonly StoreLifecycleEvent[];
}

function iso(value: string, field: string): string {
  if (new Date(value).toISOString() !== value) throw new Error(`${field} must be a canonical ISO timestamp`);
  return value;
}

function addHours(value: string, hours: number): string {
  const date = new Date(iso(value, "timestamp"));
  date.setUTCHours(date.getUTCHours() + hours);
  return date.toISOString();
}

function appendId(state: StoreSubscriptionState, event: StoreLifecycleEvent): readonly string[] {
  if (state.eventIds.includes(event.id)) throw new Error(`Duplicate store event id: ${event.id}`);
  return Object.freeze([...state.eventIds, event.id]);
}

function assertStoreAmount(event: Extract<StoreLifecycleEvent, { readonly amountMinor: number }>): void {
  if (MEMBERSHIP_ENTITLEMENTS[event.tier].monthlyPriceMinorUsd !== event.amountMinor) {
    throw new Error("Store event amount must equal the approved full monthly tier price");
  }
}

const TIER_RANK: Readonly<Record<MembershipTier, number>> = Object.freeze({ FREE: 0, INSIDER: 1, CREATOR: 2, LIFETIME_CREATOR: 3 });

export function initialStoreSubscriptionState(): StoreSubscriptionState {
  return Object.freeze({
    tier: "FREE",
    status: "ACTIVE",
    provider: null,
    paidThrough: null,
    billingAnchor: null,
    graceEndsAt: null,
    scheduledTier: null,
    eventIds: Object.freeze([])
  });
}

export function applyStoreLifecycleEvent(state: StoreSubscriptionState, event: StoreLifecycleEvent): StoreSubscriptionState {
  iso(event.at, "event at");
  const eventIds = appendId(state, event);
  if (state.tier === "LIFETIME_CREATOR" && event.type !== "LIFETIME_OWNER_GRANT") {
    return Object.freeze({ ...state, eventIds });
  }
  switch (event.type) {
    case "PURCHASE":
    case "RENEWAL":
    case "RESTORE":
      assertStoreAmount(event);
      iso(event.paidThrough, "paidThrough");
      return Object.freeze({ ...state, tier: event.tier, status: "ACTIVE", provider: event.provider, paidThrough: event.paidThrough, billingAnchor: event.type === "RENEWAL" ? state.billingAnchor : event.at, graceEndsAt: null, scheduledTier: null, eventIds });
    case "UPGRADE":
      assertStoreAmount(event);
      if (TIER_RANK[event.tier] <= TIER_RANK[state.tier]) throw new Error("Upgrade events must move to a higher paid tier");
      if (event.prorationCreditMinor !== 0) throw new Error("Upgrades charge the full new monthly price with no proration credit");
      iso(event.paidThrough, "paidThrough");
      return Object.freeze({ ...state, tier: event.tier, status: "ACTIVE", provider: event.provider, paidThrough: event.paidThrough, billingAnchor: event.at, graceEndsAt: null, scheduledTier: null, eventIds });
    case "DOWNGRADE_SCHEDULED":
      if (TIER_RANK[event.tier] >= TIER_RANK[state.tier]) throw new Error("Downgrade events must move to a lower tier");
      iso(event.effectiveAt, "effectiveAt");
      return Object.freeze({ ...state, status: "DOWNGRADE_SCHEDULED", provider: event.provider, scheduledTier: event.tier, paidThrough: event.effectiveAt, graceEndsAt: null, eventIds });
    case "CANCEL":
      return Object.freeze({ ...state, status: "CANCELS_AT_PERIOD_END", provider: event.provider, scheduledTier: "FREE", eventIds });
    case "PERIOD_ENDED":
      if (state.scheduledTier === null) throw new Error("Period end cannot change entitlement without a scheduled downgrade or cancellation");
      if (state.paidThrough === null || Date.parse(event.at) < Date.parse(state.paidThrough)) throw new Error("Period end cannot revoke access before the paid-through timestamp");
      return Object.freeze({ ...state, tier: state.scheduledTier ?? "FREE", status: "ACTIVE", provider: event.provider, paidThrough: null, graceEndsAt: null, scheduledTier: null, eventIds });
    case "PAYMENT_FAILED":
      return Object.freeze({ ...state, status: "GRACE", provider: event.provider, graceEndsAt: addHours(event.at, 48), eventIds });
    case "GRACE_EXPIRED":
      return Object.freeze({ ...state, tier: "FREE", status: "ACTIVE", provider: event.provider, paidThrough: null, graceEndsAt: null, scheduledTier: null, eventIds });
    case "REFUND":
      return Object.freeze({ ...state, tier: "FREE", status: "REFUNDED", provider: event.provider, paidThrough: null, graceEndsAt: null, scheduledTier: null, eventIds });
    case "REVERSAL":
      return Object.freeze({ ...state, tier: "FREE", status: "REVERSED", provider: event.provider, paidThrough: null, graceEndsAt: null, scheduledTier: null, eventIds });
    case "CHARGEBACK":
      return Object.freeze({ ...state, tier: "FREE", status: "CHARGEBACK", provider: event.provider, paidThrough: null, graceEndsAt: null, scheduledTier: null, eventIds });
    case "LIFETIME_OWNER_GRANT":
      if (!event.ownerDirect || event.referralCommissionMinor !== 0) throw new Error("Lifetime Creator must be owner-direct with no referral commission");
      return Object.freeze({ ...state, tier: "LIFETIME_CREATOR", status: "LIFETIME", provider: "OWNER_DIRECT", paidThrough: null, billingAnchor: null, graceEndsAt: null, scheduledTier: null, eventIds });
  }
}

export function assertSandboxStoreFixture(fixture: SandboxStoreFixture, runtime: "local" | "test" | "sandbox" | "production"): SandboxStoreFixture {
  if (fixture.sandboxOnly !== true || fixture.productionUse !== "FORBIDDEN") {
    throw new Error("Store event fixtures must be explicitly sandbox-only");
  }
  if (runtime === "production") throw new Error("Sandbox store event fixtures are forbidden in production");
  if (fixture.events.length === 0) throw new Error("Store event fixtures must include at least one lifecycle event");
  return fixture;
}
